import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { EnergyLogo } from 'src/domains/layouts/shared/assets/icons/logo/energy.logo';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { LazyImage } from 'src/domains/layouts/webview/components/common/lazyLoadingImage/LazyImage';

export const MigrationStickyTopCluster = withConfig(theme => styled('div', { label: 'MigrationStickyTopCluster' })<{ topInset: number }>`
    align-self: stretch;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    width: 100%;
    z-index: ${theme.star.zIndexGlobal.header};
    padding-top: ${({ topInset }): number => topInset}px;
`);

export const MigrationBrandBarSlot = styled('div', { label: 'MigrationBrandBarSlot' })<{ topInset: number }>`
    box-sizing: border-box;
    flex-shrink: 0;
    width: 100%;
`;

export const MigrationBrandBar = withConfig(theme => styled('div', { label: 'MigrationBrandBar' })`
    align-items: center;
    background: ${theme.star.midnightMigration.bgColorSecondary};
    box-sizing: border-box;
    display: flex;
    flex-shrink: 0;
    height: 57px;
    justify-content: flex-start;
    padding: 0 16px;
    width: 100%;
`);

export const MigrationBrandBarLogoWrap = styled('span', { label: 'MigrationBrandBarLogoWrap' })`
    box-sizing: border-box;
    display: inline-flex;
    flex-shrink: 0;
    line-height: 0;
`;

export const MigrationBrandBarLogo = styled(EnergyLogo, { label: 'MigrationBrandBarLogo' })`
    box-sizing: border-box;
    display: block;
    flex-shrink: 0;
    height: 28px;
    width: auto;
`;

export const MigrationScrollable = withConfig(theme => styled('div', { label: 'MigrationScrollable' })`
    box-sizing: border-box;
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;

    @media ${theme.star.mediaQuery.desktop} {
        margin-top: 0;
    }
`);

export const Hero = withConfig(theme => styled('section', { label: 'Hero' })`
    background: ${theme.star.midnightMigration.bgColor};
    box-sizing: border-box;
    padding: 48px 20px 56px;
    text-align: center;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        padding: 64px 32px 72px;
    }
`);

export const HeroTitleImageWrap = styled('div', { label: 'HeroTitleImageWrap' })`
    box-sizing: border-box;
    line-height: 0;
    margin: 0 auto;
    max-width: min(720px, 100%);
    width: 100%;
`;

export const HeroTitleImage = styled(LazyImage, { label: 'HeroTitleImage' })`
    box-sizing: border-box;
    display: block;
    height: auto;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    width: 100%;
`;

export const HeroSubtext = withConfig(theme => styled('p', { label: 'HeroSubtext' })`
    color: ${theme.star.midnightMigration.txtColor};
    font-size: ${theme.star.fontSize.small};
    line-height: 1.5;
    margin: 20px auto 0;
    max-width: 520px;

    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xRegular};
    }
`);

export const HeroActions = withConfig(theme => styled('div', { label: 'HeroActions' })`
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    margin-top: 28px;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        flex-direction: row;
        max-width: none;
        gap: 16px;
    }
`);

export const PrimaryCta = withConfig(theme => styled(Link, { label: 'PrimaryCta' })`
    align-items: center;
    background: ${theme.star.midnightMigration.bgColorQuaternary};
    border-radius: 8px;
    box-sizing: border-box;
    color: ${theme.star.midnightMigration.txtColorSecondary};
    display: inline-flex;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    line-height: 1.2;
    min-height: 48px;
    padding: 12px 20px;
    text-align: center;
    text-decoration: none;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        min-width: 220px;
        width: auto;
    }

    &:hover {
        opacity: 0.92;
    }
`);

export const SecondaryCta = withConfig(theme => styled(Link, { label: 'SecondaryCta' })`
    align-items: center;
    background: transparent;
    border: 2px solid ${theme.star.midnightMigration.borderColor};
    border-radius: 8px;
    box-sizing: border-box;
    color: ${theme.star.midnightMigration.txtColor};
    display: inline-flex;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    line-height: 1.2;
    min-height: 48px;
    padding: 12px 20px;
    text-align: center;
    text-decoration: none;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        min-width: 220px;
        width: auto;
    }

    &:hover {
        background: ${theme.star.midnightMigration.bgColorSeptenary};
    }
`);

export const MidSection = withConfig(theme => styled('div', { label: 'MidSection' })`
    background: ${theme.star.midnightMigration.bgColorSecondary};
    box-sizing: border-box;
    width: 100%;
`);

export const FeatureBlock = withConfig(theme => styled('div', { label: 'FeatureBlock' })<{ reverse: boolean }>`
    box-sizing: border-box;
    display: grid;
    gap: 40px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 1120px;
    padding: 40px 20px;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        align-items: center;
        gap: 48px;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        padding: 80px 32px;
        direction: ${({ reverse }) => (reverse === true ? 'rtl' : 'ltr')};
    }
`);

export const FeatureTextCol = styled('div', { label: 'FeatureTextCol' })`
    box-sizing: border-box;
    direction: ltr;
`;

export const FeatureVisualCol = styled('div', { label: 'FeatureVisualCol' })`
    align-items: center;
    box-sizing: border-box;
    direction: ltr;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 0;
`;

export const FeatureVisualFrame = withConfig(theme => styled('div', { label: 'FeatureVisualFrame' })`
    background: ${theme.star.midnightMigration.bgColorSenary};
    border-radius: 20px;
    box-sizing: border-box;
    flex-shrink: 0;
    line-height: 0;
    max-width: min(448px, 100%);
    overflow: hidden;
    width: fit-content;
`);

export const FeatureVisualImage = styled(LazyImage, { label: 'FeatureVisualImage' })`
    box-sizing: border-box;
    display: block;
    height: auto;
    max-width: 100%;
    width: auto;
`;

export const FeatureHeading = withConfig(theme => styled('h2', { label: 'FeatureHeading' })`
    color: ${theme.star.midnightMigration.txtColorTertiary};
    font-size: clamp(22px, 5vw, 32px);
    font-weight: ${theme.star.fontWeight.bold};
    letter-spacing: 1px;
    line-height: 1.15;
    margin: 0;
    text-transform: uppercase;
`);

export const FeatureTerms = withConfig(theme => styled('p', { label: 'FeatureTerms' })`
    color: ${theme.star.midnightMigration.txtColorQuaternary};
    font-size: 11px;
    line-height: 1.45;
    margin: 16px 0 0;
    max-width: 520px;

    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xSmall};
    }
`);

export const ClaimCta = withConfig(theme => styled(Link, { label: 'ClaimCta' })`
    align-items: center;
    background: ${theme.star.midnightMigration.bgColorQuinary};
    border-radius: 8px;
    box-sizing: border-box;
    color: ${theme.star.midnightMigration.txtColor};
    display: inline-flex;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    line-height: 1.2;
    margin-top: 24px;
    min-height: 48px;
    padding: 12px 28px;
    text-align: center;
    text-decoration: none;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        width: auto;
    }

    &:hover {
        opacity: 0.92;
    }
`);

export const TestimonialsWrap = withConfig(theme => styled('section', { label: 'TestimonialsWrap' })`
    background: ${theme.star.midnightMigration.bgColorSecondary};
    box-sizing: border-box;
    padding: 24px 16px 56px;
    width: 100%;

    @media ${theme.star.mediaQuery.tablet} {
        padding: 0 32px;
    }
`);

export const TestimonialsHeading = withConfig(theme => styled('h2', { label: 'TestimonialsHeading' })`
    color: ${theme.star.midnightMigration.txtColorTertiary};
    font-size: 32px;
    font-weight: ${theme.star.fontWeight.bold};
    letter-spacing: 2px;
    line-height: 1.2;
    margin: 0 auto 28px;
    max-width: 1120px;
    text-align: center;
    text-transform: uppercase;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: 40px;
    }
`);

export const TestimonialsGrid = styled('div', { label: 'TestimonialsGrid' })`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-left: -16px;
    margin-right: -16px;
    max-width: none;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
    scroll-padding-inline: 16px;
    scroll-snap-type: x proximity;
    width: auto;

    @media screen and (min-width: 1025px) {
        display: grid;
        gap: 16px;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        margin-left: 0;
        margin-right: 0;
        max-width: 1120px;
        margin-inline: auto;
        overflow-x: visible;
        overscroll-behavior-x: auto;
        padding-left: 0;
        padding-right: 0;
        scroll-padding-inline: 0;
        scroll-snap-type: none;
        width: 100%;
    }
`;

export const TestimonialCardRoot = withConfig(theme => styled('article', { label: 'TestimonialCardRoot' })`
    background: ${theme.star.midnightMigration.bgColorTertiary};
    border-radius: 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: 0 0 min(320px, calc(100vw - 48px));
    height: 344px;
    padding: 22px 24px;
    scroll-snap-align: start;

    @media screen and (min-width: 1025px) {
        flex: none;
        height: 344px;
        min-height: 0;
    }
`);

export const TestimonialCardMain = styled('div', { label: 'TestimonialCardMain' })`
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
`;

export const TestimonialCardFooter = styled('div', { label: 'TestimonialCardFooter' })`
    box-sizing: border-box;
    flex-shrink: 0;
    margin-top: auto;
`;

export const TestimonialTitle = withConfig(theme => styled('h3', { label: 'TestimonialTitle' })`
    color: ${theme.star.midnightMigration.txtColorTertiary};
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3;
    margin: 0;
`);

export const TestimonialBody = withConfig(theme => styled('p', { label: 'TestimonialBody' })`
    color: ${theme.star.midnightMigration.txtColorQuinary};
    flex: 1 1 auto;
    font-size: ${theme.star.fontSize.small};
    line-height: 1.45;
    margin: 12px 0 0;
    min-height: 0;
    overflow-y: auto;
`);

export const StarsRow = withConfig(theme => styled('div', { label: 'StarsRow' })`
    color: ${theme.star.midnightMigration.txtColorSeptenary};
    font-size: 16px;
    letter-spacing: 2px;
    line-height: 1;
    margin-top: 0;
`);

export const TestimonialAuthor = withConfig(theme => styled('p', { label: 'TestimonialAuthor' })`
    color: ${theme.star.midnightMigration.txtColorSenary};
    font-size: ${theme.star.fontSize.xSmall};
    margin: 8px 0 0;
`);
